import { ApiService } from '../../services/api_service';
import { ExcelTool } from '../../core/excel/excel_tool';
import { formatDefaultExport } from '../../core/table/WTableFormatter';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import * as React from 'react';
import { StructureTool } from '../../core/forms/helpers/structure_tool';
import { WActionButton } from '../../core/elements/WActionButton';
import { WFormsTableWrapper } from '../../core/table/WFormsTableWrapper';
import * as XLSX from 'xlsx';
import { DataService } from '../../services/data_service';
import { Datenmenge } from './Datenmenge';
import { getCO2Scope3StructureAllgemein, getCO2Scope3StructureNachgelagert, getCO2Scope3StructureVorgelagert } from '../dateneingabe/functions/getCO2Scope3Structure';
import { getVsmeBasisEntities } from '../dateneingabe/assistent/items/assistent_config_vsme_basis';
import { getVsmeBpmEntities } from '../dateneingabe/assistent/items/assistent_config_vsme_bpm';
import { getVsmePatEntities } from '../dateneingabe/assistent/items/assistent_config_vsme_pat';
import { b64ToBlob } from '../../core/elements/helper/b64ToBlob';
import { WSpinner } from '../../core/elements/spinner/WSpinner';
import * as Moment from 'moment';

export interface ZusatztoolsExportProps {
    data: any;
}

interface ZusatztoolsExportState {
    doExport: boolean;
    galleryData: any;
}

export class ZusatztoolsExport extends React.Component<ZusatztoolsExportProps, ZusatztoolsExportState> {

    constructor(props: any) {
        super(props);
        this.state = {
            doExport: false,
            galleryData: undefined
        };

        this.setDataForExport = this.setDataForExport.bind(this);
        this.buildFile = this.buildFile.bind(this);

        this.loadEnergienEntities();
        this.loadCO2Scope3Entities();
        this.loadVsmeEntities();
    }

    @lazyInject(ApiService) private apiService: ApiService;
    @lazyInject(DataService) private dataService: DataService;

    private entities: Array<string> = [];
    private dataForExport: any = {};
    private colsForExport: any = {};
    private buildHandle: number = undefined;

    componentDidMount(): void {
        this.loadGalleryData();
    }

    loadGalleryData() {
        this.dataService.loadData(IDs.ENT_GALLERY, {}, {}, (result: any) => {
            const galleryData: any = {};
            if (result[IDs.ENT_GALLERY]) {
                result[IDs.ENT_GALLERY].forEach((item: any) => {
                    const k = item.parent_entity;
                    if (!galleryData[k]) {
                        galleryData[k] = [];
                    }
                    galleryData[k].push(item);
                });
            }
            this.setState({ galleryData });
        }, IDs.MOD_ETOOL);
    }

    loadEnergienEntities() {
        const entities = [
            IDs.ENT_ENERGIEDATEN_STROM,
            IDs.ENT_ENERGIEDATEN_EIGENSTROM,
            IDs.ENT_ENERGIEDATEN_WAERME,
            IDs.ENT_ENERGIEDATEN_KRAFTSTOFFE,
            IDs.ENT_ENERGIEDATEN_WASSER,
            IDs.ENT_KLIMAGASE,
            IDs.ENT_BETRIEBSDATEN,
            IDs.ENT_SONSTIGE_DATEN,
            IDs.ENT_MASCHINEN,
            IDs.ENT_FAHRZEUGE,
            IDs.ENT_STANDORT,
            IDs.ENT_ZAEHLER,
            // Erneuerbare Energien
            IDs.ENT_ANLAGEN_EIGENSTROM_WINDKRAFT,
            IDs.ENT_ANLAGEN_EIGENSTROM_PHOTOVOLTAIK,
            IDs.ENT_ANLAGEN_EIGENSTROM_KWK,
            IDs.ENT_ANLAGEN_EIGENSTROM_SOLARTHERMIE,
            IDs.ENT_ANLAGEN_EIGENSTROM_WAERMEPUMPEN,
            IDs.ENT_ANLAGEN_EIGENSTROM_BIOMASSE
        ];
        this.entities = [...this.entities, ...entities];
    }

    loadCO2Scope3Entities() {
        const bereichKey = this.apiService.getBereichKey();
        const scope3Structur = [
            ...getCO2Scope3StructureVorgelagert(bereichKey),
            ...getCO2Scope3StructureNachgelagert(bereichKey),
            ...getCO2Scope3StructureAllgemein()
        ];
        scope3Structur.forEach((s: any) => {
            if (!s.ignore && this.entities.indexOf(`co2_scope3_${s.key}`) === -1) {
                this.entities.push(`co2_scope3_${s.key}`);
            }
        });
    }

    loadVsmeEntities() {
        const konfig = this.props.data[IDs.ENT_KONFIGURATION][0];
        if (konfig.vsme_orig === 1) {
            const vsmeStructur = [
                ...getVsmeBasisEntities(),
                ...getVsmeBpmEntities(),
                ...getVsmePatEntities(),
            ];
            vsmeStructur.filter((s: any) => s.key).forEach((s: any) => {
                if (this.entities.indexOf(s.key) === -1) {
                    this.entities.push(s.key);
                }
            });
        }
    }

    render(): JSX.Element {
        const listStyle: JSX.Element = <span style={{ marginRight: -4, left: -8, position: 'relative' }}>-</span>;
        return (
            <div style={{ fontSize: '9pt', padding: 20 }}>
                <b style={{ fontSize: '10pt' }}>E-Tool EXPORT - Daten exportieren:</b><br />
                <br />
                Mit dieser Funktion können Sie alle Informationen, die Sie im <b>Dateneingabebereich</b> des E-Tools hinterlegt haben
                (Zahlen, Bemerkungen, Rechnungen etc.) aus dem Tool extrahieren und als .zip-Datei auf Ihrem lokalen Datenträger speichern.
                Die Funktion ist nicht zur Auswertung oder zur Offline-Bearbeitung von Daten vorgesehen, sondern dient explizit deren
                Sicherung.<br />
                <br />
                <u>Bitte beachten</u>:<br />
                <ul style={{ lineHeight: '1.5em', margin: '4px 0 0 8px', padding: 0, listStyleType: 'none' }}>
                    <li>{listStyle}Die Daten können nicht ins E-Tool zurückgespielt werden.</li>
                    <li>
                        (<span style={{ fontSize: 'large', lineHeight: 0, marginRight: 2 }}>&#x2192;</span>
                        Die Daten im Webportal bleiben weiterhin erhalten.)
                    </li>
                    <li>{listStyle}Der Export kann einen Moment dauern</li>
                    <li>
                        (<span style={{ fontSize: 'large', lineHeight: 0, marginRight: 2 }}>&#x2192;</span>
                        normalerweise nur ein paar Sekunden)
                    </li>
                </ul>
                <div style={{ marginTop: 15 }}>
                    {this.state.doExport
                        ? <b><i>Daten werden exportiert...</i></b>
                        :
                        <WActionButton
                            styleProps={{ padding: '1px 8px', whiteSpace: 'nowrap' }}
                            label='Daten exportieren'
                            insideText='Daten exportieren'
                            key='export'
                            action='export'
                            onAction={() => this.setState({ doExport: true })}
                        />
                    }
                </div>
                {this.state.doExport && this.renderExport()}
                <br /><br />
                {this.state.galleryData ? <Datenmenge
                    data={this.props.data}
                    galleryData={this.state.galleryData}
                    entities={this.entities}
                /> : null}
                {this.state.doExport && <WSpinner />}
            </div>
        );
    }

    renderExport(): JSX.Element {
        if (!this.state.galleryData) {
            return <b>Vorbereitung ....</b>;
        }
        return <div>
            {this.entities.map((ent: string) => this.renderExportEntity(ent))}
        </div>;
    }

    renderExportEntity(entity: string): JSX.Element {
        const data = this.props.data[entity] || [];
        if (data.length > 0) {
            // console.log('renderExportEntity', entity, data);
            return (
                <div style={{ display: 'none' }}>
                    <WFormsTableWrapper
                        entity={entity}
                        data={data}
                        module={IDs.MOD_ETOOL}
                        customClass={'Custom-ETA'}
                        passDataForExport={(data: Array<any>, cols: Array<any>) => this.setDataForExport(entity, data, cols)}
                    />
                </div>
            );
        }
        return null;
    }

    setDataForExport(entity: string, data: Array<any>, cols: Array<any>): void {
        // console.log('setDataForExport', entity, data, cols);
        if (!this.dataForExport[entity]) {
            this.dataForExport[entity] = data;
            this.colsForExport[entity] = cols;
        }
        const handle = window.setTimeout(() => {
            if (handle === this.buildHandle) {
                const files = this.prepareFiles();
                this.apiService.call(
                    'resolveFiles',
                    { files },
                    (data_res: string, suc: boolean) => {
                        if (suc) {
                            // Die Dateinamen einfügen
                            this.setFilenames(JSON.parse(data_res));
                            const xls_b64 = this.buildFile();
                            this.exportFiles(xls_b64, files);
                        }
                    },
                    IDs.MOD_ETOOL);
            }
        }, 2000);
        this.buildHandle = handle;
    }

    setFilenames(map: any): void {
        Object.keys(this.dataForExport).forEach((key: string) => {
            const items = this.dataForExport[key];
            const cols = this.colsForExport[key];
            items.forEach((item: any) => {
                cols.forEach((col: any) => {
                    if (col.type && col.type === 'FILE') {
                        if (item[col.accessor] && map[item[col.accessor] + '']) {
                            item._original[col.accessor] = map[item[col.accessor] + ''];
                            item[col.accessor] = map[item[col.accessor] + ''];
                        }
                    }
                });
            });
        });
    }

    prepareFiles(): any {
        const files: any = {};
        const galleryData = this.state.galleryData;
        Object.keys(this.dataForExport).forEach((key: string) => {
            const items = this.dataForExport[key];
            const cols = this.colsForExport[key];
            items.forEach((item: any) => {
                cols.forEach((col: any) => {
                    if (col.type && col.type === 'FILE') {
                        if (item[col.accessor]) {
                            files[item[col.accessor]] = `${this.getPathForKey(key)}/`;
                            if (item.jahr) {
                                files[item[col.accessor]] += item.jahr + '/';
                            }
                        }
                    }
                    // Gallery checken
                    const gallery_key = key + '_' + item._original.tid;
                    if (galleryData[gallery_key] && galleryData[gallery_key].length > 0) {
                        galleryData[gallery_key].forEach((g_item: any) => {
                            files[g_item.foto] = `${this.getPathForKey(key)}/`;
                            if (item.jahr) {
                                files[g_item.foto] += item.jahr + '/';
                            }
                        });
                    } else {
                        //  console.log('No gallery: ' + gallery_key, item);
                    }
                });
            });
        });
        // Jetzt die Reste der Gallerie
        Object.keys(galleryData).forEach((gallery_key: string) => {
            galleryData[gallery_key].forEach((g_item: any) => {
                if (!files[g_item.foto]) {
                    files[g_item.foto] = `${this.getPathForKey(gallery_key)}/`;
                }
            });
        });
        // console.log('prepareFiles', files);
        return files;
    }

    exportFiles(xls_b64: string, files: any): void {
        this.apiService.call(
            'exportFiles',
            { files, xls_b64 },
            (data: string, suc: boolean) => {
                if (suc) {
                    try {
                        const filename = `Dateiexport_${Moment().format('YYYY-MM-DD')}.zip`;
                        b64ToBlob(data, 'application/octet-stream', (blob: any) => {
                            saveAs(blob, filename);
                        });
                        this.dataForExport = {};
                    } catch (error) {
                        console.log('error: ', error);
                    }
                }
                this.setState({ doExport: false });
            },
            IDs.MOD_ETOOL
        );
    }

    buildFile() {
        const strucTool = new StructureTool(IDs.MOD_ETOOL);
        const et = new ExcelTool();
        const cols: Array<Array<any>> = [];
        const data: Array<Array<any>> = [];
        const names: Array<string> = [];
        Object.keys(this.dataForExport).forEach((key: string) => {
            cols.push(this.colsForExport[key]);
            data.push(this.dataForExport[key]);
            const struct = strucTool.getStructure(IDs.MOD_ETOOL, key);
            // xlsx.js -> error: Sheet names cannot exceed 31 chars, ':' not allowed
            const sheetname = (struct.TITEL ? struct.TITEL : key).replaceAll(':in', '').replaceAll(':', '').substring(0, 30);
            names.push(sheetname);
        });
        const base64 = et.dataMultiToWb('Gesamtexport', cols, data, names, (sheetData: XLSX.WorkSheet) => {
            formatDefaultExport(sheetData);
        }, undefined /*Export Options*/);
        return base64;
    }

    getPathForKey(key: string): string {
        switch (key) {
            case IDs.ENT_ENERGIEDATEN_WAERME:
                return 'Hochgeladene Rechnungen Wärme';
            case IDs.ENT_ENERGIEDATEN_STROM:
                return 'Hochgeladene Rechnungen Strom';
            case IDs.ENT_ENERGIEDATEN_WASSER:
                return 'Hochgeladene Rechnungen Wasser';
            case IDs.ENT_ENERGIEDATEN_EIGENSTROM:
                return 'Hochgeladene Rechnungen Eigenstrom';
            case IDs.ENT_ENERGIEDATEN_KRAFTSTOFFE:
                return 'Hochgeladene Rechnungen Kraftstoffe';
            case IDs.ENT_MASCHINEN:
                return 'Hochgeladene Fotos Maschinen';
            case IDs.ENT_ZAEHLER:
                return 'Hochgeladene Fotos Zähler';
            case IDs.ENT_FAHRZEUGE:
                return 'Hochgeladene Fotos Fahrzeuge';
            case IDs.ENT_STANDORT:
                return 'Hochgeladene Fotos Standorte';
            case IDs.ENT_KLIMAGASE:
                return 'Hochgeladene Rechnungen Klimagase';
            // CO2_SCOPE3
            case IDs.ENT_CO2_SCOPE3_GUETER_EINKAUF:
                return 'Dokumente CO2-Scope3-1';
            case IDs.ENT_CO2_SCOPE3_GUETER_KAPITAL:
                return 'Dokumente CO2-Scope3-2';
            case IDs.ENT_CO2_SCOPE3_TRANSPORT_VORGELAGERT:
                return 'Dokumente CO2-Scope3-4';
            case IDs.ENT_CO2_SCOPE3_ABFALL:
                return 'Dokumente CO2-Scope3-5';
            case IDs.ENT_CO2_SCOPE3_REISEN:
                return 'Dokumente CO2-Scope3-6';
            case IDs.ENT_CO2_SCOPE3_PENDELN:
                return 'Dokumente CO2-Scope3-7';
            case IDs.ENT_CO2_SCOPE3_ANGEMIETETE_SACHANLAGEN:
                return 'Dokumente CO2-Scope3-8';
            case IDs.ENT_CO2_SCOPE3_TRANSPORT_NACHGELAGERT:
                return 'Dokumente CO2-Scope3-9';
            case IDs.ENT_CO2_SCOPE3_PRODUKTE_VERARBEITUNG:
                return 'Dokumente CO2-Scope3-10';
            case IDs.ENT_CO2_SCOPE3_PRODUKTE_NUTZUNG:
                return 'Dokumente CO2-Scope3-11';
            case IDs.ENT_CO2_SCOPE3_PRODUKTE_LEBENSZYKLUS:
                return 'Dokumente CO2-Scope3-12';
            case IDs.ENT_CO2_SCOPE3_VERMIETETE_SACHANLAGEN:
                return 'Dokumente CO2-Scope3-13';
            case IDs.ENT_CO2_SCOPE3_VERMIETETE_SACHANLAGEN_RAEUMLICHKEITEN:
                return 'Dokumente CO2-Scope3-13.1';
            case IDs.ENT_CO2_SCOPE3_VERMIETETE_SACHANLAGEN_FAHRZEUGE:
                return 'Dokumente CO2-Scope3-13.2';
            case IDs.ENT_CO2_SCOPE3_VERMIETETE_SACHANLAGEN_TECHNIK:
                return 'Dokumente CO2-Scope3-13.3';
            case IDs.ENT_CO2_SCOPE3_FRANCHISE:
                return 'Dokumente CO2-Scope3-14';
            case IDs.ENT_CO2_SCOPE3_INVESTITION:
                return 'Dokumente CO2-Scope3-15';
            case IDs.ENT_CO2_SCOPE3_EMITTENTEN:
                return 'Dokumente CO2-Scope3-16';
            // VSME
            case IDs.ENT_VSME_B1:
                return 'Dokumente VSME-B1';
            case IDs.ENT_VSME_B2:
                return 'Dokumente VSME-B2';
            case IDs.ENT_VSME_B3:
                return 'Dokumente VSME-B3';
            case IDs.ENT_VSME_B4:
                return 'Dokumente VSME-B4';
            case IDs.ENT_VSME_B5:
                return 'Dokumente VSME-B5';
            case IDs.ENT_VSME_B6:
                return 'Dokumente VSME-B6';
            case IDs.ENT_VSME_B7:
                return 'Dokumente VSME-B7';
            case IDs.ENT_VSME_B8:
                return 'Dokumente VSME-B8';
            case IDs.ENT_VSME_B9:
                return 'Dokumente VSME-B9';
            case IDs.ENT_VSME_B10:
                return 'Dokumente VSME-B10';
            case IDs.ENT_VSME_B11:
                return 'Dokumente VSME-B11';
            case IDs.ENT_VSME_B12:
                return 'Dokumente VSME-B12';
            case IDs.ENT_VSME_BPM1:
                return 'Dokumente VSME-BP1';
            case IDs.ENT_VSME_BPM2:
                return 'Dokumente VSME-BP2';
            case IDs.ENT_VSME_BPM3:
                return 'Dokumente VSME-BP3';
            case IDs.ENT_VSME_BPM3_SCOPE1:
                return 'Dokumente VSME-BP3-Scope1';
            case IDs.ENT_VSME_BPM3_SCOPE2:
                return 'Dokumente VSME-BP3-Scope2';
            case IDs.ENT_VSME_BPM4:
                return 'Dokumente VSME-BP4';
            case IDs.ENT_VSME_BPM5:
                return 'Dokumente VSME-BP5';
            case IDs.ENT_VSME_BPM5_C:
                return 'Dokumente VSME-BP5-C';
            case IDs.ENT_VSME_BPM5_E:
                return 'Dokumente VSME-BP5-E';
            case IDs.ENT_VSME_BPM6:
                return 'Dokumente VSME-BP6';
            case IDs.ENT_VSME_BPM7:
                return 'Dokumente VSME-BP7';
            case IDs.ENT_VSME_BPM8:
                return 'Dokumente VSME-BP8';
            case IDs.ENT_VSME_BPM9:
                return 'Dokumente VSME-BP9';
            case IDs.ENT_VSME_BPM10:
                return 'Dokumente VSME-BP10';
            case IDs.ENT_VSME_BPM11:
                return 'Dokumente VSME-BP11';
            case IDs.ENT_VSME_N1:
                return 'Dokumente VSME-N1';
            case IDs.ENT_VSME_N2:
                return 'Dokumente VSME-N2';
            case IDs.ENT_VSME_N3:
                return 'Dokumente VSME-N3';
            case IDs.ENT_VSME_N4:
                return 'Dokumente VSME-N4';
            case IDs.ENT_VSME_N5:
                return 'Dokumente VSME-N5';
            // Erneuerbare Energien
            case IDs.ENT_ANLAGEN_EIGENSTROM_WINDKRAFT:
                return 'Dokumente Windkraft';
            case IDs.ENT_ANLAGEN_EIGENSTROM_PHOTOVOLTAIK:
                return 'Dokumente Photovoltaik';
            case IDs.ENT_ANLAGEN_EIGENSTROM_KWK:
                return 'Dokumente KWK';
            case IDs.ENT_ANLAGEN_EIGENSTROM_SOLARTHERMIE:
                return 'Dokumente Solarthermie';
            case IDs.ENT_ANLAGEN_EIGENSTROM_WAERMEPUMPEN:
                return 'Dokumente Wärmepumpen';
            case IDs.ENT_ANLAGEN_EIGENSTROM_BIOMASSE:
                return 'Dokumente Biomasse';
        }

        return 'Sonstige Dokumente';
    }
}
